import {Bonus} from 'com/gongxigames/math/core/game/engine/bonus/Bonus';
import {Action} from 'com/gongxigames/math/core/game/enums/Action';
import {Pay} from 'com/gongxigames/math/core/game/Pay';
import {Spin} from 'com/gongxigames/math/core/game/Spin';
import {Module} from '@gongxigames/framework/slot/fragments/module/Module';
import {DI} from '@gongxigames/framework/slot/configs/DI';
import {GlobalEventsMap, WinMeterFinishedValue} from '@gongxigames/framework/slot/events';
import {inject} from '@gongxigames/framework/core';
import {WinPresentationUtils} from '@gongxigames/framework/slot/services/WinPresentationUtils';
import {SoundPlayer} from '@gongxigames/framework/slot/services/SoundPlayer';
import {WinMeterEvents} from '@gongxigames/framework/slot/modules/winmeter';
import {FlowModuleEvents} from '@gongxigames/framework/slot/modules/flow';
import {SpinButtonEvents} from '@gongxigames/framework/slot/modules/spinbutton';
import {BigWinEvents} from '@gongxigames/framework/slot/modules/bigwin';
import {PickBonusEvents} from '@gongxigames/framework/slot/modules/pick';
import {ReelsEvents} from '@gongxigames/framework/slot/modules/reels';
import {AutoplayEvents} from '@gongxigames/framework/slot/modules/autoplay';
import {GenericPopupEvents, GenericPopupType} from '@gongxigames/framework/slot/modules/genericPopup';
import {BuyBonusPopUpEvents} from '@gongxigames/framework/slot/modules/buyBonus';
import {WinPresentationBasicState} from '@gongxigames/framework/slot/modules/winpresentation/states/WinPresentationBasicState';
import {WinPresentationEvents} from '@gongxigames/framework/slot/modules/winpresentation/WinPresentationEvents';
import {WinPresentationFreeSpinsBasicState} from '@gongxigames/framework/slot/modules/winpresentation/states/WinPresentationFreeSpinsBasicState';
import {WinPresentationFreeSpinsInitialState} from '@gongxigames/framework/slot/modules/winpresentation/states/WinPresentationFreeSpinsInitialState';
import {WinPresentationFreeSpinsPaysState} from '@gongxigames/framework/slot/modules/winpresentation/states/WinPresentationFreeSpinsPaysState';
import {WinPresentationInitialState} from '@gongxigames/framework/slot/modules/winpresentation/states/WinPresentationInitialState';
import {WinPresentationPaysState} from '@gongxigames/framework/slot/modules/winpresentation/states/WinPresentationPaysState';
import {WinPresentationRestoreState} from '@gongxigames/framework/slot/modules/winpresentation/states/WinPresentationRestoreState';
import {WinPresentationPickState} from '@gongxigames/framework/slot/modules/winpresentation/states/WinPresentationPickState';
import {WinPresentationView} from '@gongxigames/framework/slot/modules/winpresentation/WinPresentationView';
import {WinPresentationViewEvents} from '@gongxigames/framework/slot/modules/winpresentation/WinPresentationViewEvents';
import {KeypadControlsEvents} from '@gongxigames/framework/slot/modules/keypadControls/KeypadControlsEvents';

export class WinPresentationModule extends Module<WinPresentationView> {
    public static readonly NAME: symbol = Symbol('WinPresentationModule');

    public static readonly INITIAL_STATE: string = 'initial';
    public static readonly PAYS_STATE: string = 'pays';
    public static readonly BASIC_STATE: string = 'basic';
    public static readonly FREESPINS_BASIC_STATE: string = 'freespinsbasic';
    public static readonly FREESPINS_INITIAL_STATE: string = 'freespinsinitial';
    public static readonly FREESPINS_PAYS_STATE: string = 'freespinspays';
    public static readonly AFTER_RESTORE_STATE: string = 'afterfsrestore';
    public static readonly PICK_STATE: string = 'pick';

    @inject(DI.WinPresentationUtils)
    protected _winPresentationUtils!: WinPresentationUtils;

    private _isIndividualWinMustBePaused = false;
    private _winMeterFinished = false;
    private _allPaysAnimationsFinished = false;
    private _bigWinFinished = false;
    private _isBigWinInCurrentRound = false;
    private _isInFreeSpins = false;

    public restore(): void {
        if (this.logic.nextAction !== Action.SPIN) {
            this._isInFreeSpins = this.logic.getFreeGamesIndex() !== -1;
        }
    }

    public play(isFreeSpins: boolean): void {
        this.view.play();
        this.view.isInFreeSpins = isFreeSpins;

        const from = this.getStartValueForWinMeter(isFreeSpins);
        const to = this.getFinishValueForWinMeter(isFreeSpins);

        if (to && this.isCountUp(isFreeSpins)) {
            // in transition between states (except into Base Game) or for retrigger fs we always should show only initial flow
            // and count flow meter during this time
            if (this.isInitialCountUp(isFreeSpins)) {
                this.setupInitialWinMeterCountUp(from, to, isFreeSpins);
            } else {
                this.setupRegularWinMeterCountUp(from, to, isFreeSpins);
            }
        }
    }

    protected getStartValueForWinMeter(_isFreeSpins: boolean): number {
        return this.logic.previousSpinRoundWin;
    }

    protected getFinishValueForWinMeter(isFreeSpins: boolean): number {
        return isFreeSpins ? this.logic.getTotalWin() : this.logic.getTotalWin() - this.logic.previousSpinRoundWin;
    }

    protected isCountUp(_isFreeSpins: boolean): boolean {
        return true;
    }

    protected isInitialCountUp(_isFreeSpins: boolean): boolean {
        const {nextAction, previousAction} = this.logic;

        return nextAction !== previousAction && nextAction !== Action.SPIN || this.logic.getRetriggerCount() > 0;
    }

    protected setupInitialWinMeterCountUp(from: number, to: number, _isFreeSpins: boolean): void {
        const duration = this._winPresentationUtils.getWinConfig().initial.time;
        const winMeterConfig = this._winPresentationUtils.getWinTypeConfigForSpinWin();

        this.setupCountUpWinMeter(from, to, duration, winMeterConfig?.noCountUpAnimation);
    }

    protected setupRegularWinMeterCountUp(from: number, to: number, _isFreeSpins: boolean): void {
        const winMeterConfig = this._winPresentationUtils.getWinTypeConfigForSpinWin();

        if (this._winPresentationUtils.isBigWin() || !winMeterConfig) {
            return;
        }

        this.setupCountUpWinMeter(from, to, winMeterConfig.duration, winMeterConfig.noCountUpAnimation);
    }

    protected setupCountUpWinMeter(from: number, to: number, duration = 0, noCounterAnimation = false): void {
        if (noCounterAnimation) {
            this.eventManager.emit(WinMeterEvents.SET_VALUE, to);
            this.onWinMeterFinished();
        } else {
            this.eventManager.emit(WinMeterEvents.COUNT_UP, from, to, duration, this.onWinMeterFinished.bind(this));
        }
    }

    public stop(): void {
        this.view.stop();
    }

    public stopMeter(clearMeterAfterPause: boolean): void {
        this.eventManager.emit(WinMeterEvents.STOP_AFTER_SHOWING_FINAL_VALUE, this.logic.getTotalWin(), clearMeterAfterPause);
    }

    public stopInFreeSpins(): void {
        this.view.stop();
        this.eventManager.emit(WinMeterEvents.SET_VALUE, this.logic.getTotalWin(), true);
    }

    protected setupEvents(): void {
        super.setupEvents();

        this.onView(WinPresentationViewEvents.INITIAL_ANIMATION_STARTED, this.onInitialAnimationStarted);
        this.onView(WinPresentationViewEvents.PAY_ANIMATION_STARTED, this.onPayAnimationStarted);
        this.onView(WinPresentationViewEvents.ALL_PAY_ANIMATIONS_FINISHED, this.onAllPayAnimationsFinished);

        this.onViewCurrentStateHandle(WinPresentationViewEvents.INITIAL_ANIMATION_FREESPINS_FINISHED);
        this.onViewCurrentStateHandle(WinPresentationViewEvents.INITIAL_ANIMATION_FINISHED);

        this.onCurrentStateHandle(FlowModuleEvents.WON);
        this.onCurrentStateHandle(SpinButtonEvents.SPIN_BUTTON_PRESSED);
        this.onCurrentStateHandle(BigWinEvents.FINISHED);
        this.onCurrentStateHandle(PickBonusEvents.FINISHED);
        this.onCurrentStateHandle(FlowModuleEvents.BIG_WIN_AFTER_BONUS_GAME_STARTED);
        this.onCurrentStateHandle(FlowModuleEvents.SKIP_PICK_BONUS_AWARD);
        this.onCurrentStateHandle(FlowModuleEvents.START_PICK_BONUS_AWARD);
        this.onCurrentStateHandle('General.WinPresentation.WIN_METER_FINISHED');
        this.onCurrentStateHandle('General.WinPresentation.WIN_METER_STOPPED');

        this.onCurrentStateHandle(FlowModuleEvents.SHOULD_START_INDIVIDUAL_WIN_AFTER_POPUP_FREESPINS);
        this.onCurrentStateHandle(FlowModuleEvents.SHOULD_START_INDIVIDUAL_WIN_FREESPINS);

        this.on(BigWinEvents.WIN_METER_FINISHED, this.onBigWinMeterFinished);
        this.on(BigWinEvents.FINISHED, this.onInternalBigWinFinished, -1);
        this.on(ReelsEvents.REELS_SPINNING_STARTED, this.onStartSpinning);
        this.on(ReelsEvents.ALL_REELS_STOPPED, this.onAllReelsStopped);
        this.on(PickBonusEvents.STARTED, this.onPickBonusStarted);
        this.on(BigWinEvents.STARTED, this.onBigWinStarted);
        this.on(FlowModuleEvents.FREE_SPINS_FINISHED, this.onFreeSpinsFinished);
        this.on(WinPresentationEvents.ALL_PAY_ANIMATIONS_FINISHED, this.onPayAnimationsFinished);
        this.on(AutoplayEvents.AUTOPLAY_START_SPIN, this.onAutoplaySpinStarted);

        this.on(FlowModuleEvents.WP_RESTORE, this.onRestoreWinPresentation);
        this.on(FlowModuleEvents.RESPIN_RESTORED, this.onRespinRestored);
        this.on(FlowModuleEvents.FREESPINS_RESTORED, this.onFreespinsRestored);
        this.on(KeypadControlsEvents.TOTAL_BET_CHANGED, this.onBetChanged);

        this.on(GenericPopupEvents.INTRO_STARTED, this.onPopupStarted);
        this.on(GenericPopupEvents.POPUP_CLOSED, this.onPopupClosed);
        this.on(GenericPopupEvents.FADE_OUT_STARTED, this.onPopupFadeOutStarted);

        this.on(BuyBonusPopUpEvents.BUY_BUTTON_CLICKED, this.onBuyButtonClicked);
    }

    protected onBuyButtonClicked(): void {
        if (this.currentState.name !== WinPresentationModule.BASIC_STATE) {
            this.gotoState(WinPresentationModule.BASIC_STATE);
            this.onWinMeterFinished();
            this.eventManager.emit(WinMeterEvents.STOP);
            this.view.stop();
        }
    }

    protected onPopupStarted(id: symbol, type: symbol): void {
        if (type === GenericPopupType.FREESPIN_INTRO) {
            this.onFreeSpinIntroStarted();
        }

        if (type === GenericPopupType.FREESPIN_OUTRO) {
            this.onFreespinsOutroStarted();
        }

        if (type === GenericPopupType.FREESPIN_RETRIGGER) {
            this.onFreeSpinRetriggerStarted();
        }

        this.currentState.handle(id, type);
    }

    protected onPopupClosed(id: symbol, type: symbol): void {
        this.currentState.handle(id, type);
    }

    protected onPopupFadeOutStarted(id: symbol, type: symbol): void {
        this.currentState.handle(id, type);
    }

    protected setupViews(): void {
        this.setView(WinPresentationView.NAME);
    }

    protected setupStates(): void {
        this.addState(new WinPresentationBasicState(this, WinPresentationModule.BASIC_STATE));
        this.addState(new WinPresentationInitialState(this, WinPresentationModule.INITIAL_STATE));
        this.addState(new WinPresentationPaysState(this, WinPresentationModule.PAYS_STATE));
        this.addState(new WinPresentationFreeSpinsBasicState(this, WinPresentationModule.FREESPINS_BASIC_STATE));
        this.addState(new WinPresentationFreeSpinsInitialState(this, WinPresentationModule.FREESPINS_INITIAL_STATE));
        this.addState(new WinPresentationFreeSpinsPaysState(this, WinPresentationModule.FREESPINS_PAYS_STATE));
        this.addState(new WinPresentationRestoreState(this, WinPresentationModule.AFTER_RESTORE_STATE));
        this.addState(new WinPresentationPickState(this, WinPresentationModule.PICK_STATE));

        this.gotoState(WinPresentationModule.BASIC_STATE);
    }

    protected onRestoreWinPresentation(eventID: symbol, spin?: Spin): void {
        const spinData = spin ? spin : this.logic.spinData;

        this.view.setPaysForRestore(spinData);
        this.gotoState(WinPresentationModule.AFTER_RESTORE_STATE);
    }

    protected onFreeSpinsFinished(): void {
        this._isInFreeSpins = false;
        this.gotoState(WinPresentationModule.BASIC_STATE);
    }

    protected onBigWinStarted(): void {
        this.isIndividualWinMustBePaused = true;
        this.currentState.handle(BigWinEvents.STARTED);
        this.eventManager.emit(WinPresentationEvents.PAUSED);
        this._isBigWinInCurrentRound = true;
    }

    protected onBigWinMeterFinished(): void {
        this.winMeterFinished = true;
    }

    private onInternalBigWinFinished(): void {
        this._bigWinFinished = true;

        if (this.isSkipAllPayWinAnimation()) {
            this.view.skipAllPayWinAnimations();
        }
    }

    protected onStartSpinning(): void {
        this.currentState.handle(ReelsEvents.REELS_SPINNING_STARTED);
        this.winMeterFinished = false;
        this.view.resetPaysData();
        this._bigWinFinished = false;
        this._isBigWinInCurrentRound = false;
    }

    protected onAllReelsStopped(): void {
        this.isIndividualWinMustBePaused = false;
    }

    protected onFreeSpinIntroStarted(): void {
        this._isInFreeSpins = true;
        this.resetWinPresentation();
        this.gotoState(WinPresentationModule.FREESPINS_BASIC_STATE);
    }

    protected resetWinPresentation(): void {
        this.view.stop();
        this.isIndividualWinMustBePaused = true;
        this.eventManager.emit(WinMeterEvents.STOP_FINAL_VALUE_TIMER);
    }

    protected onRespinRestored(): void {
    }

    protected onFreespinsRestored(): void {
        this._isInFreeSpins = true;
        this.gotoState(WinPresentationModule.FREESPINS_BASIC_STATE);
    }

    protected onFreeSpinRetriggerStarted(): void {
        this.resetWinPresentation();
        this.view.setPaysData(this.logic.spinData.pays);
    }

    protected onPickBonusStarted(): void {
        const winBeforeBonusStarted = this.logic.getTotalWin();

        if (winBeforeBonusStarted) {
            this.eventManager.emit(WinMeterEvents.SET_VALUE, winBeforeBonusStarted);
        } else {
            this.eventManager.emit(WinMeterEvents.STOP_FINAL_VALUE_TIMER);
        }
        this.currentState.handle(PickBonusEvents.STARTED);

        this.gotoState(WinPresentationModule.PICK_STATE);
    }

    public playPickWin(): void {
        const from = this.logic.previousSpinRoundWin;
        const to = this.logic.getTotalWin();
        const win = to - from;
        const winTypes = this._winPresentationUtils.getWinTypeConfig(win);
        const winTypeConfigBuyFeature = this._winPresentationUtils.getSpecialWinTypeConfigBuyFeature(win);

        if (winTypes) {
            this.view.setPaysData(this.logic.spinData.pays);
            this.view.playWinSound(win);
            this.setupCountUpWinMeter(from, to, winTypes.duration, winTypeConfigBuyFeature?.noCountUpAnimation);

            if (!this._winPresentationUtils.isBigWin(win)) {
                this.view.playPayAnimation();
            }
        }
    }

    protected onFreespinsOutroStarted(): void {
        this.stopInFreeSpins();
        this.eventManager.emit(WinMeterEvents.STOP_FINAL_VALUE_TIMER);
    }

    protected onInitialAnimationStarted(listenerID: string, pays: Pay[]): void {
        const bonusId: number = this.logic.spinData.bonusId;
        let isBlocking = false;

        if (bonusId === Bonus.FREE_GAMES_BONUS || bonusId === Bonus.PICK_BONUS || bonusId === Bonus.SELECT_BONUS) {
            isBlocking = true;
        }

        this.eventManager.emit(WinPresentationEvents.INITIAL_ANIMATION_STARTED, pays, isBlocking);
        this.setupIndividualAnimationPause();
    }

    protected setupIndividualAnimationPause(): void {
        if (this.logic.nextAction === Action.PICK || this.logic.nextAction === Action.SELECT) {
            this.isIndividualWinMustBePaused = true;
        }
    }

    protected onPayAnimationStarted(listenerID: string, pay: Pay): void {
        this.eventManager.emit(WinPresentationEvents.PAY_ANIMATION_STARTED, pay);

        if (this.isSkipAllPayWinAnimation()) {
            this.view.skipAllPayWinAnimations();
        }
    }

    protected isSkipAllPayWinAnimation(): boolean {
        let isSkip = this.winMeterFinished && this._isInFreeSpins;

        if (this._isBigWinInCurrentRound) {
            isSkip &&= this._bigWinFinished;
        }

        return isSkip;
    }

    protected onWinMeterFinished(): void {
        this.eventManager.emit(SoundPlayer.UNDUCK_MUSIC);

        this.emitWinMeterFinished();

        if (this.allPaysAnimationsFinished) {
            this.eventManager.emit(WinPresentationEvents.PAY_ANIMATIONS_AND_WIN_METER_FINISHED);
        }
    }

    protected onPayAnimationsFinished(): void {
        this.allPaysAnimationsFinished = true;

        if (this.winMeterFinished) {
            this.eventManager.emit(WinPresentationEvents.PAY_ANIMATIONS_AND_WIN_METER_FINISHED);
        }
    }

    protected onAutoplaySpinStarted(): void {
        this.isIndividualWinMustBePaused = true;
    }

    protected onAllPayAnimationsFinished(): void {
        this.eventManager.emit(WinPresentationEvents.ALL_PAY_ANIMATIONS_FINISHED);
    }

    protected onBetChanged(): void {
        if (this.currentState.name !== WinPresentationModule.BASIC_STATE) {
            this.currentState.handle(KeypadControlsEvents.TOTAL_BET_CHANGED);
            this.gotoState(WinPresentationModule.BASIC_STATE);
            this.onWinMeterFinished();
            this.eventManager.emit(WinMeterEvents.STOP);
            this.view.stop();
        }
    }

    public get isIndividualWinMustBePaused(): boolean {
        return this._isIndividualWinMustBePaused;
    }

    public set isIndividualWinMustBePaused(value: boolean) {
        this._isIndividualWinMustBePaused = value;
    }

    public isBigWin(): boolean {
        return this._winPresentationUtils.isBigWin();
    }

    public get winMeterFinished(): boolean {
        return this._winMeterFinished;
    }

    public set winMeterFinished(value: boolean) {
        this._winMeterFinished = value;
    }

    public get allPaysAnimationsFinished(): boolean {
        return this._allPaysAnimationsFinished;
    }

    public set allPaysAnimationsFinished(value: boolean) {
        this._allPaysAnimationsFinished = value;
    }

    public emitWinMeterFinished(): void {
        this.getEventManager<GlobalEventsMap>().emit('General.WinPresentation.WIN_METER_FINISHED', this.getWinMeterFinishedValue());
        this.winMeterFinished = true;
    }

    private getWinMeterFinishedValue(): WinMeterFinishedValue {
        const {previousSpinRoundWin} = this.logic;
        const totalWin = this.logic.getTotalWin();
        const win = totalWin - previousSpinRoundWin;

        return {win, totalWin, isBigWin: this.logic.isBigWin()};
    }
}
